import { type Locale } from '../i18n'

/**
 * Grille tarifaire fulfillment — livraison en point relais (Mondial Relay).
 * Prix TOUT COMPRIS par commande expédiée (transport + pick & pack), HTVA.
 * Colonnes = volume mensuel de commandes ; lignes = tranche de poids du colis.
 * Source : grille commerciale Yaslan (octobre 2026). Au-delà de 20 kg : sur devis.
 *
 * Prix d'une cellule = P&P de base (volume) + majoration P&P (poids)
 *                    + Mondial Relay Belgique/Luxembourg refacturé (poids, surcharges comprises).
 */
export const VOLUME_COLS = [
  { min: 100, max: 999, pickPack: 2.95 },
  { min: 1000, max: 1999, pickPack: 2.8 },
  { min: 2000, max: 4999, pickPack: 2.6 },
  { min: 5000, max: 10000, pickPack: 2.4 },
] as const

const WEIGHT_BRACKETS = [
  { minKg: 0, maxKg: 1, pickPackSurcharge: 0, transport: 3.3 },
  { minKg: 1, maxKg: 2, pickPackSurcharge: 0, transport: 3.46 },
  { minKg: 2, maxKg: 3, pickPackSurcharge: 0.1, transport: 3.65 },
  { minKg: 3, maxKg: 5, pickPackSurcharge: 0.25, transport: 3.77 },
  { minKg: 5, maxKg: 7, pickPackSurcharge: 0.45, transport: 3.94 },
  { minKg: 7, maxKg: 10, pickPackSurcharge: 0.7, transport: 4.06 },
  { minKg: 10, maxKg: 15, pickPackSurcharge: 1.0, transport: 4.77 },
  { minKg: 15, maxKg: 20, pickPackSurcharge: 1.5, transport: 5.25 },
] as const

/** Arrondi au centime — évite les artefacts flottants (2.95 + 3.3 = 6.249999…). */
const cents = (n: number) => Math.round(n * 100) / 100

export const WEIGHT_ROWS = WEIGHT_BRACKETS.map((w) => ({
  minKg: w.minKg,
  maxKg: w.maxKg,
  prices: VOLUME_COLS.map((v) => cents(v.pickPack + w.pickPackSurcharge + w.transport)),
}))

/** Poids maximum couvert par la grille publique (au-delà : sur devis). */
export const MAX_WEIGHT_KG = WEIGHT_ROWS[WEIGHT_ROWS.length - 1].maxKg

/**
 * Ajustement SKU — s'ajoute au prix all-in par commande selon la taille
 * du catalogue. Jusqu'à 25 SKU : aucun supplément.
 */
export const SKU_TIERS = [
  { label: '0 – 25', fee: 0 },
  { label: '25 – 50', fee: 0.05 },
  { label: '50 – 100', fee: 0.2 },
  { label: '> 100', fee: 0.5 },
] as const

/**
 * Prix d'entrée public « à partir de » : la cellule 0-1 kg / < 1000 commandes
 * (6,25 €), PAS le meilleur tarif volume — décision client du 2026-07-17.
 */
export const ENTRY_PRICE = WEIGHT_ROWS[0].prices[0]

/** Prix plafond (cellule la plus chère + ajustement SKU max) — JSON-LD highPrice. */
export const HIGHEST_PRICE = cents(
  WEIGHT_ROWS[WEIGHT_ROWS.length - 1].prices[0] + SKU_TIERS[SKU_TIERS.length - 1].fee,
)

/** Index de colonne volume pour un nombre de commandes/mois donné. */
export function volumeColIndex(orders: number): number {
  for (let i = 0; i < VOLUME_COLS.length; i++) {
    if (orders <= VOLUME_COLS[i].max) return i
  }
  return VOLUME_COLS.length - 1
}

/** Prix tout compris par commande pour (volume mensuel, index de tranche de poids). */
export function pricePerOrder(orders: number, weightIndex: number): number {
  return WEIGHT_ROWS[weightIndex].prices[volumeColIndex(orders)]
}

/** Locale BCP-47 pour Intl (formats monétaires / nombres). */
export const INTL_LOCALE: Record<Locale, string> = {
  fr: 'fr-BE',
  en: 'en-GB',
  nl: 'nl-BE',
}

export type PricingContent = {
  // SEO
  seoTitle: string
  metaDescription: string
  breadcrumb: string

  // Hero
  badge: string
  titleLead: string
  titleHighlight: string
  titleTail: string
  intro: string
  heroPoints: string[]
  heroCtaCalc: string
  heroCtaQuote: string

  // Histoire du fondateur
  founderKicker: string
  founderTitleLead: string
  founderTitleHighlight: string
  founderP1: string
  founderP2: string
  founderQuote: string
  founderLink: string
  founderPhotoAlt: string

  // « Que facture Yaslan ? » — passage citable + 2 coûts
  billTitle: string
  billText: string
  transportTitle: string
  transportDesc: string
  transportPoints: string[]
  pickpackTitle: string
  pickpackDesc: string
  pickpackPoints: string[]
  notBilledTitle: string
  notBilledItems: string[]

  // « Combien ça coûte ? » — passage citable
  costTitle: string
  costText: string

  // Calculateur
  calcKicker: string
  calcTitle: string
  calcSubtitle: string
  /** Encadré sous le sous-titre du calculateur : logiciel gratuit, seuls les services logistiques sont facturés. */
  connectNote: string
  calcOrdersLabel: string
  calcOrdersUnit: string
  calcWeightLabel: string
  calcOverWeightLabel: string
  calcSkuLabel: string
  calcResultLabel: string
  calcResultUnit: string
  calcResultIncludes: string
  calcMonthlyLabel: string
  calcMonthlyUnit: string
  calcNextTierHint: string
  calcQuoteTitle: string
  calcQuoteText: string
  calcCta: string
  calcUnderMin: string
  calcUnderMinCta: string

  // Lien guide (sous le passage « combien ça coûte »)
  guideLabel: string
  guideSlug: string

  // FAQ
  faqTitle: string
  faqs: { q: string; a: string }[]

  // Maillage interne
  linksTitle: string
  linksSubtitle: string
  links: {
    key: 'service.storage' | 'service.pickpack' | 'service.shipping' | 'service.returns' | 'fulfillment'
    label: string
    desc: string
  }[]
  linksCta: string

  // CTA final
  ctaBadge: string
  ctaTitleLead: string
  ctaTitleHighlight: string
  ctaCopy: string
  ctaPrimary: string
  ctaSecondary: string
  trust1: string
  trust2: string
  trust3: string
}

export const PRICING_CONTENT: Record<Locale, PricingContent> = {
  fr: {
    seoTitle: 'Tarifs logistique e-commerce transparents | Yaslan',
    metaDescription:
      'Tarifs Yaslan : vous payez transport + pick & pack, jamais de frais de réception ni de stockage caché. Calculez votre prix par commande dès 6,25 € HTVA.',
    breadcrumb: 'Tarifs',

    badge: 'Tarifs · 100 % transparents',
    titleLead: 'Des tarifs ',
    titleHighlight: '100 % transparents',
    titleTail: ', sans frais cachés.',
    intro:
      "Yaslan a été fondé par un ancien e-commerçant, avec une règle simple : vous ne payez que deux choses — le transport et le pick & pack. Pas de frais de réception, pas de frais de stockage opaques, pas de coûts cachés.",
    heroPoints: [
      'Dès 6,25 € HTVA par commande, tout compris',
      'Sans abonnement, sans frais de dossier',
      'Tarif dégressif selon votre volume mensuel',
    ],
    heroCtaCalc: 'Calculer mon tarif',
    heroCtaQuote: 'Demander un devis',

    founderKicker: 'Pourquoi cette transparence',
    founderTitleLead: 'Créé par un e-commerçant qui en avait assez des ',
    founderTitleHighlight: 'factures illisibles',
    founderP1:
      "Avant de créer Yaslan, notre fondateur a vendu en ligne et expédié des dizaines de milliers de colis. Il a connu les factures 3PL à rallonge : frais de réception, supplément d'étiquette, forfait de stockage flou, frais administratifs apparus de nulle part. Chaque mois, la marge fondait dans des lignes que personne ne savait expliquer.",
    founderP2:
      "Yaslan est né de cette frustration. Votre facture ne contient que ce que vous avez réellement expédié : si un coût n'est pas dans la grille, il n'existe pas.",
    founderQuote:
      'Vous ne payez que deux choses : le transport et le pick & pack. Tout le reste, c’est notre travail — pas votre problème.',
    founderLink: 'Découvrir notre histoire',
    founderPhotoAlt: 'Le fondateur de Yaslan',

    billTitle: 'Que facture Yaslan ?',
    billText:
      "Deux postes, rien d'autre : le transport de vos colis et la préparation de vos commandes. La plupart des 3PL ajoutent des frais fixes peu transparents — la facture finale dépasse souvent le devis de 20 %. Chez Yaslan, ce qui n'est pas dans la grille n'existe pas.",
    transportTitle: 'Le transport',
    transportDesc:
      'Votre colis, livré au meilleur tarif : nous comparons 30+ transporteurs et négocions avec le volume de tout notre entrepôt.',
    transportPoints: [
      '30+ transporteurs comparés à chaque commande',
      'Point relais, domicile, express ou international',
      'Tarif dégressif selon votre volume mensuel',
    ],
    pickpackTitle: 'Le pick & pack',
    pickpackDesc:
      'Picking, contrôle, carton, calage et étiquette : chaque commande est préparée en moins de 4 h, à votre image.',
    pickpackPoints: [
      'Inclus dans le prix par commande',
      'Emballage personnalisé à votre marque',
      'Contrôle qualité systématique',
    ],
    notBilledTitle: 'Ce que vous ne payez jamais',
    notBilledItems: [
      'Réception & mise en stock de vos marchandises',
      'Stockage de vos produits',
      'Abonnement WMS ou accès au dashboard',
      'Intégration Shopify, WooCommerce ou autre',
      'Onboarding, création de compte, frais de dossier',
      'Minimum mensuel garanti ou surcharge saisonnière',
    ],

    costTitle: 'Combien coûte le fulfillment e-commerce en Belgique ?',
    costText:
      "Sur le marché, un fulfillment externalisé revient généralement entre 3,50 € et 8 € par commande — mais la facture finale dépasse souvent le devis une fois ajoutés frais de réception, abonnement WMS, stockage et minimums mensuels. Chez Yaslan, le prix démarre à 6,25 € HTVA par commande, transport et pick & pack inclus, et n'évolue qu'avec trois variables : le poids du colis, votre volume mensuel et la taille de votre catalogue.",

    calcKicker: 'Calculateur',
    calcTitle: 'Estimez votre coût par commande',
    calcSubtitle:
      'Déplacez le curseur, choisissez le poids moyen de vos colis : votre tarif tout compris s’affiche instantanément. Aucune inscription, aucun e-mail requis.',
    connectNote:
      "Le logiciel Yaslan Connect est gratuit. Seuls les services logistiques (réception, stockage, préparation, expédition) sont facturés par Yaslan, selon la grille ci-dessous. La facturation se fait directement par Yaslan, en dehors de Shopify.",
    calcOrdersLabel: 'Commandes par mois',
    calcOrdersUnit: 'commandes/mois',
    calcWeightLabel: 'Poids moyen du colis',
    calcOverWeightLabel: '> 20 kg',
    calcSkuLabel: 'Nombre de références (SKU)',
    calcResultLabel: 'Votre tarif tout compris',
    calcResultUnit: '/ commande',
    calcResultIncludes: 'Transport + pick & pack inclus · HTVA',
    calcMonthlyLabel: 'Budget logistique mensuel estimé',
    calcMonthlyUnit: '/ mois',
    calcNextTierHint: 'À {orders} commandes/mois, ce colis passe à {price} l’unité.',
    calcQuoteTitle: 'Au-delà de 20 kg : sur devis',
    calcQuoteText:
      'Les colis lourds ou volumineux méritent une vraie analyse : transporteur adapté, palettisation éventuelle, meilleur rapport poids/prix. On vous répond sous 4 h ouvrées.',
    calcCta: 'Obtenir mon devis précis',
    calcUnderMin: 'Moins de 100 commandes par mois ?',
    calcUnderMinCta: 'Parlez-nous quand même — on vous oriente honnêtement',

    guideLabel: 'Aller plus loin : combien coûte réellement un 3PL ? Lire le guide complet',
    guideSlug: 'tarifs-logistique-e-commerce',

    faqTitle: 'Questions fréquentes sur nos tarifs',
    faqs: [
      {
        q: 'Comment sont calculés vos tarifs logistique e-commerce ?',
        a: "Deux postes seulement : le transport et le pick & pack, réunis en un prix unique par commande. Ce prix dépend du poids du colis, de votre volume mensuel et de la taille de votre catalogue (SKU) — plus vous expédiez, plus le tarif unitaire baisse. Aucun frais de réception, d'onboarding ni de dossier ne s'ajoute.",
      },
      {
        q: 'Y a-t-il un nombre minimum de commandes pour travailler avec Yaslan ?',
        a: "Non, aucun minimum contractuel. Le calculateur démarre à 100 commandes/mois car c'est le seuil où notre grille dégressive devient la plus intéressante. En dessous, contactez-nous : on vous répond honnêtement, même si la meilleure solution n'est pas nous.",
      },
      {
        q: 'Combien coûte la livraison en point relais avec Yaslan ?',
        a: "À partir de 6,25 € HTVA par commande, tout compris (transport + pick & pack). Le tarif évolue selon le poids du colis — de 0 à 20 kg — et devient dégressif avec votre volume mensuel. Au-delà de 25 références au catalogue, un ajustement de 0,05 € à 0,50 € par commande s'applique.",
      },
      {
        q: 'Facturez-vous des frais de réception ou de mise en stock ?',
        a: 'Non. La réception de vos marchandises, le contrôle qualité et la mise en stock sont inclus, sans frais. Vous ne payez que le transport et le pick & pack de vos commandes expédiées.',
      },
      {
        q: 'Facturez-vous la réception des palettes ? (beaucoup de prestataires logistiques facturent 5 à 10 € par palette)',
        a: "Non : la réception de vos palettes — déchargement, contrôle et mise en stock — est incluse, à 0 €. Là où de nombreux prestataires facturent 5 à 10 € par palette reçue, chez Yaslan la réception n'est jamais facturée. Vous ne payez que le transport et le pick & pack de vos commandes expédiées.",
      },
      {
        q: 'Et le stockage, comment est-il facturé ?',
        a: "Il ne l'est pas séparément. Réception, mise en stock et inventaire sont compris : vous ne payez que le transport et le pick & pack de chaque commande expédiée. Les différentes méthodes de stockage sont détaillées sur notre page Stockage.",
      },
      {
        q: 'Le tarif change-t-il selon le poids ou la taille des colis ?',
        a: "Oui, selon le poids : la grille couvre huit tranches, de 0–1 kg à 15–20 kg. Au-delà de 20 kg ou pour un colis très volumineux, nous établissons un devis avec le transporteur le plus adapté.",
      },
      {
        q: 'Puis-je obtenir un devis personnalisé après le calculateur ?',
        a: "Oui. Le calculateur donne un ordre de grandeur fiable ; le devis précis intègre vos destinations, vos options de livraison (domicile, express, international) et votre emballage personnalisé. Réponse sous 4 h ouvrées.",
      },
    ],

    linksTitle: 'Ce que ces tarifs recouvrent',
    linksSubtitle:
      'Chaque commande passe par nos quatre services — tous inclus dans la même logique : transparente et dégressive.',
    links: [
      { key: 'service.storage', label: 'Stockage', desc: 'Réception, mise en stock, inventaire — compris.' },
      { key: 'service.pickpack', label: 'Pick & Pack', desc: 'Préparation < 4 h, à votre image.' },
      { key: 'service.shipping', label: 'Expédition 24h', desc: 'Cut-off 15h, 30+ transporteurs comparés.' },
      { key: 'service.returns', label: 'Retours', desc: 'Portail marque blanche, remise en stock rapide.' },
      { key: 'fulfillment', label: 'Fulfillment DNVB & D2C', desc: 'La logistique pensée pour votre marque.' },
    ],
    linksCta: 'Voir le service',

    ctaBadge: 'Devis gratuit · Réponse sous 4 h ouvrées',
    ctaTitleLead: 'Un prix clair vaut mieux que ',
    ctaTitleHighlight: 'mille promesses',
    ctaCopy:
      'Envoyez-nous vos volumes et vos poids moyens : vous recevez une grille personnalisée, complète et sans astérisque. Comparez-la ligne par ligne avec votre 3PL actuel.',
    ctaPrimary: 'Demander mon devis',
    ctaSecondary: 'Réserver un appel de 30 min',
    trust1: 'Réponse sous 4 h ouvrées',
    trust2: 'Sans abonnement ni engagement',
    trust3: 'Grille complète, aucun coût caché',
  },

  en: {
    seoTitle: 'Transparent E-commerce Fulfilment Pricing | Yaslan',
    metaDescription:
      'Yaslan pricing: you pay shipping + pick & pack, never receiving fees or hidden storage costs. Calculate your per-order price from €6.25 excl. VAT.',
    breadcrumb: 'Pricing',

    badge: 'Pricing · 100% transparent',
    titleLead: 'Pricing that is ',
    titleHighlight: '100% transparent',
    titleTail: ' — no hidden fees.',
    intro:
      'Yaslan was founded by a former e-commerce seller with one simple rule: you only ever pay for two things — shipping and pick & pack. No receiving fees, no vague storage charges, no hidden costs.',
    heroPoints: [
      'From €6.25 excl. VAT per order, all-in',
      'No subscription, no set-up fees',
      'Rates decrease as your monthly volume grows',
    ],
    heroCtaCalc: 'Calculate my rate',
    heroCtaQuote: 'Request a quote',

    founderKicker: 'Why this transparency',
    founderTitleLead: 'Built by an e-commerce seller who was done with ',
    founderTitleHighlight: 'unreadable invoices',
    founderP1:
      'Before founding Yaslan, our founder sold online and shipped tens of thousands of parcels. He knew the endless 3PL invoices all too well: receiving fees, label surcharges, vague storage plans, admin costs appearing out of nowhere. Every month, margin evaporated into lines nobody could explain.',
    founderP2:
      'Yaslan was born from that frustration. Your invoice only contains what you actually shipped: if a cost is not in the grid, it does not exist.',
    founderQuote:
      'You pay for two things only: shipping and pick & pack. Everything else is our job — not your problem.',
    founderLink: 'Read our story',
    founderPhotoAlt: 'The founder of Yaslan',

    billTitle: 'What does Yaslan charge for?',
    billText:
      'Two items, nothing else: shipping your parcels and preparing your orders. Most 3PLs pile on opaque fixed fees — the final invoice often runs 20% over the quote. At Yaslan, if it is not in the grid, it does not exist.',
    transportTitle: 'Shipping',
    transportDesc:
      'Your parcel, delivered at the best rate: we compare 30+ carriers and negotiate with the volume of our entire warehouse.',
    transportPoints: [
      '30+ carriers compared on every order',
      'Pickup point, home, express or international',
      'Rates decrease with your monthly volume',
    ],
    pickpackTitle: 'Pick & pack',
    pickpackDesc:
      'Picking, quality check, box, protection and label: every order prepared in under 4 hours, in your brand’s image.',
    pickpackPoints: [
      'Included in the per-order price',
      'Packaging customised to your brand',
      'Systematic quality control',
    ],
    notBilledTitle: 'What you never pay for',
    notBilledItems: [
      'Receiving & putaway of your goods',
      'Storage of your products',
      'WMS subscription or dashboard access',
      'Shopify, WooCommerce or other integrations',
      'Onboarding, account set-up, admin fees',
      'Guaranteed monthly minimum or seasonal surcharge',
    ],

    costTitle: 'How much does e-commerce fulfilment cost in Belgium?',
    costText:
      'On the market, outsourced fulfilment typically comes to between €3.50 and €8 per order — but the final invoice often exceeds the quote once receiving fees, WMS subscriptions, storage and monthly minimums are added. At Yaslan, pricing starts at €6.25 excl. VAT per order, shipping and pick & pack included, and only moves with three variables: parcel weight, your monthly volume and the size of your catalogue.',

    calcKicker: 'Calculator',
    calcTitle: 'Estimate your cost per order',
    calcSubtitle:
      'Move the slider, pick your average parcel weight: your all-in rate appears instantly. No sign-up, no e-mail required.',
    connectNote:
      "The Yaslan Connect software is free. Only the logistics services (receiving, storage, picking, shipping) are billed by Yaslan, according to the grid below. Billing is handled directly by Yaslan, outside Shopify.",
    calcOrdersLabel: 'Orders per month',
    calcOrdersUnit: 'orders/month',
    calcWeightLabel: 'Average parcel weight',
    calcOverWeightLabel: '> 20 kg',
    calcSkuLabel: 'Number of SKUs',
    calcResultLabel: 'Your all-in rate',
    calcResultUnit: '/ order',
    calcResultIncludes: 'Shipping + pick & pack included · excl. VAT',
    calcMonthlyLabel: 'Estimated monthly logistics budget',
    calcMonthlyUnit: '/ month',
    calcNextTierHint: 'At {orders} orders/month, this parcel drops to {price} each.',
    calcQuoteTitle: 'Above 20 kg: custom quote',
    calcQuoteText:
      'Heavy or bulky parcels deserve real analysis: the right carrier, possible palletisation, the best weight-to-price ratio. We reply within 4 business hours.',
    calcCta: 'Get my exact quote',
    calcUnderMin: 'Fewer than 100 orders per month?',
    calcUnderMinCta: 'Talk to us anyway — we’ll point you in the right direction',

    guideLabel: 'Go further: how much does a 3PL really cost? Read the full guide',
    guideSlug: 'e-commerce-logistics-pricing-how-much-does-a-3pl-really-cost-in-2025',

    faqTitle: 'Frequently asked questions about our pricing',
    faqs: [
      {
        q: 'How is your e-commerce logistics pricing calculated?',
        a: 'Two items only: shipping and pick & pack, combined into a single per-order price. That price depends on parcel weight, your monthly volume and the size of your catalogue (SKUs) — the more you ship, the lower the unit rate. No receiving, onboarding or admin fees are ever added.',
      },
      {
        q: 'Is there a minimum number of orders to work with Yaslan?',
        a: 'No contractual minimum. The calculator starts at 100 orders/month because that is where our volume-based grid becomes most interesting. Below that, get in touch: we will answer honestly, even if the best solution is not us.',
      },
      {
        q: 'How much does pickup-point delivery cost with Yaslan?',
        a: 'From €6.25 excl. VAT per order, all-in (shipping + pick & pack). The rate varies with parcel weight — 0 to 20 kg — and decreases with your monthly volume. Above 25 SKUs in your catalogue, an adjustment of €0.05 to €0.50 per order applies.',
      },
      {
        q: 'Do you charge receiving or putaway fees?',
        a: 'No. Receiving your goods, quality control and putaway are included, free of charge. You only pay shipping and pick & pack on the orders you ship.',
      },
      {
        q: 'Do you charge for pallet receiving? (many logistics providers bill €5–10 per pallet)',
        a: 'No: receiving your pallets — unloading, quality control and putaway — is included, at €0. Where many providers charge €5 to €10 per pallet received, Yaslan never bills receiving. You only pay shipping and pick & pack on the orders you ship.',
      },
      {
        q: 'And storage — how is it billed?',
        a: 'It is not billed separately. Receiving, putaway and inventory are included: you only pay shipping and pick & pack on each shipped order. The different storage methods are detailed on our Storage page.',
      },
      {
        q: 'Does the price change with parcel weight or size?',
        a: 'Yes, with weight: the grid covers eight brackets, from 0–1 kg to 15–20 kg. Above 20 kg, or for very bulky parcels, we prepare a quote with the most suitable carrier.',
      },
      {
        q: 'Can I get a custom quote after using the calculator?',
        a: 'Yes. The calculator gives a reliable order of magnitude; the exact quote factors in your destinations, delivery options (home, express, international) and custom packaging. Reply within 4 business hours.',
      },
    ],

    linksTitle: 'What these rates cover',
    linksSubtitle:
      'Every order flows through our four services — all built on the same logic: transparent and volume-based.',
    links: [
      { key: 'service.storage', label: 'Storage', desc: 'Receiving, putaway, inventory — included.' },
      { key: 'service.pickpack', label: 'Pick & Pack', desc: 'Prepared in under 4 h, in your brand’s image.' },
      { key: 'service.shipping', label: 'Next-day delivery', desc: '3pm cut-off, 30+ carriers compared.' },
      { key: 'service.returns', label: 'Returns', desc: 'White-label portal, fast restocking.' },
      { key: 'fulfillment', label: 'DNVB & D2C fulfilment', desc: 'Logistics built for your brand.' },
    ],
    linksCta: 'View service',

    ctaBadge: 'Free quote · Reply within 4 business hours',
    ctaTitleLead: 'A clear price beats ',
    ctaTitleHighlight: 'a thousand promises',
    ctaCopy:
      'Send us your volumes and average weights: you receive a personalised, complete rate card with no asterisks. Compare it line by line with your current 3PL.',
    ctaPrimary: 'Request my quote',
    ctaSecondary: 'Book a 30-min call',
    trust1: 'Reply within 4 business hours',
    trust2: 'No subscription, no commitment',
    trust3: 'Full rate card, zero hidden costs',
  },

  nl: {
    seoTitle: 'Transparante fulfilment tarieven | Yaslan',
    metaDescription:
      'Tarieven van Yaslan: je betaalt enkel transport + pick & pack, nooit ontvangst- of verborgen opslagkosten. Bereken je prijs per bestelling vanaf € 6,25 excl. btw.',
    breadcrumb: 'Tarieven',

    badge: 'Tarieven · 100% transparant',
    titleLead: 'Tarieven die ',
    titleHighlight: '100% transparant',
    titleTail: ' zijn — zonder verborgen kosten.',
    intro:
      'Yaslan werd opgericht door een ex-webshopondernemer, met één simpele regel: je betaalt maar twee dingen — transport en pick & pack. Geen ontvangstkosten, geen vage opslagkosten, geen verborgen kosten.',
    heroPoints: [
      'Vanaf € 6,25 excl. btw per bestelling, alles inbegrepen',
      'Geen abonnement, geen dossierkosten',
      'Degressief tarief naargelang je maandvolume',
    ],
    heroCtaCalc: 'Bereken mijn tarief',
    heroCtaQuote: 'Offerte aanvragen',

    founderKicker: 'Waarom deze transparantie',
    founderTitleLead: 'Opgericht door een webshopondernemer die genoeg had van ',
    founderTitleHighlight: 'onleesbare facturen',
    founderP1:
      'Voor hij Yaslan oprichtte, verkocht onze oprichter zelf online en verzond hij tienduizenden pakketten. Hij kende de eindeloze 3PL-facturen maar al te goed: ontvangstkosten, toeslagen per etiket, vage opslagforfaits, administratiekosten uit het niets. Elke maand verdampte de marge in lijnen die niemand kon uitleggen.',
    founderP2:
      'Yaslan is uit die frustratie geboren. Je factuur bevat enkel wat je werkelijk hebt verzonden: staat een kost niet in de tabel, dan bestaat hij niet.',
    founderQuote:
      'Je betaalt maar twee dingen: transport en pick & pack. Al de rest is ons werk — niet jouw probleem.',
    founderLink: 'Ontdek ons verhaal',
    founderPhotoAlt: 'De oprichter van Yaslan',

    billTitle: 'Wat factureert Yaslan?',
    billText:
      'Twee posten, niets anders: het transport van je pakketten en de verwerking van je bestellingen. De meeste 3PL’s stapelen ondoorzichtige vaste kosten op — de eindfactuur ligt vaak 20% boven de offerte. Bij Yaslan: staat het niet in de tabel, dan bestaat het niet.',
    transportTitle: 'Het transport',
    transportDesc:
      'Jouw pakket, geleverd aan het beste tarief: we vergelijken 30+ vervoerders en onderhandelen met het volume van ons hele magazijn.',
    transportPoints: [
      '30+ vervoerders vergeleken bij elke bestelling',
      'Afhaalpunt, thuis, express of internationaal',
      'Degressief tarief naargelang je maandvolume',
    ],
    pickpackTitle: 'De pick & pack',
    pickpackDesc:
      'Picking, controle, doos, opvulling en etiket: elke bestelling wordt in minder dan 4 uur klaargemaakt, in jouw merkstijl.',
    pickpackPoints: [
      'Inbegrepen in de prijs per bestelling',
      'Verpakking gepersonaliseerd met jouw merk',
      'Systematische kwaliteitscontrole',
    ],
    notBilledTitle: 'Wat je nooit betaalt',
    notBilledItems: [
      'Ontvangst & inslag van je goederen',
      'Opslag van je producten',
      'WMS-abonnement of toegang tot het dashboard',
      'Integratie met Shopify, WooCommerce of andere',
      'Onboarding, accountaanmaak, dossierkosten',
      'Gegarandeerd maandelijks minimum of seizoenstoeslag',
    ],

    costTitle: 'Wat kost e-commerce fulfilment in België?',
    costText:
      'Op de markt kost uitbestede fulfilment doorgaans tussen € 3,50 en € 8 per bestelling — maar de eindfactuur overstijgt vaak de offerte zodra ontvangstkosten, WMS-abonnementen, opslag en maandelijkse minima erbij komen. Bij Yaslan start de prijs vanaf € 6,25 excl. btw per bestelling, transport en pick & pack inbegrepen, en beweegt hij enkel met drie variabelen: het gewicht van het pakket, je maandvolume en de grootte van je catalogus.',

    calcKicker: 'Calculator',
    calcTitle: 'Bereken je kost per bestelling',
    calcSubtitle:
      'Verschuif de slider, kies het gemiddelde gewicht van je pakketten: je all-in tarief verschijnt meteen. Geen registratie, geen e-mail nodig.',
    connectNote:
      "De software Yaslan Connect is gratis. Alleen de logistieke diensten (ontvangst, opslag, picking, verzending) worden door Yaslan gefactureerd, volgens het onderstaande tarief. De facturatie gebeurt rechtstreeks door Yaslan, buiten Shopify.",
    calcOrdersLabel: 'Bestellingen per maand',
    calcOrdersUnit: 'bestellingen/maand',
    calcWeightLabel: 'Gemiddeld pakketgewicht',
    calcOverWeightLabel: '> 20 kg',
    calcSkuLabel: 'Aantal referenties (SKU’s)',
    calcResultLabel: 'Jouw all-in tarief',
    calcResultUnit: '/ bestelling',
    calcResultIncludes: 'Transport + pick & pack inbegrepen · excl. btw',
    calcMonthlyLabel: 'Geschat maandelijks logistiek budget',
    calcMonthlyUnit: '/ maand',
    calcNextTierHint: 'Vanaf {orders} bestellingen/maand zakt dit pakket naar {price} per stuk.',
    calcQuoteTitle: 'Boven 20 kg: op offerte',
    calcQuoteText:
      'Zware of volumineuze pakketten verdienen een echte analyse: de juiste vervoerder, eventuele palletisering, de beste gewicht-prijsverhouding. We antwoorden binnen 4 werkuren.',
    calcCta: 'Vraag mijn exacte offerte',
    calcUnderMin: 'Minder dan 100 bestellingen per maand?',
    calcUnderMinCta: 'Praat toch met ons — we wijzen je eerlijk de weg',

    guideLabel: 'Ga dieper: wat kost een 3PL werkelijk? Lees de volledige gids',
    guideSlug: 'e-commerce-logistieke-tarieven-wat-kost-een-3pl-werkelijk-in-2025',

    faqTitle: 'Veelgestelde vragen over onze tarieven',
    faqs: [
      {
        q: 'Hoe worden jullie fulfilment tarieven berekend?',
        a: 'Slechts twee posten: transport en pick & pack, samengebracht in één prijs per bestelling. Die prijs hangt af van het gewicht van het pakket, je maandvolume en de grootte van je catalogus (SKU’s) — hoe meer je verzendt, hoe lager het stukstarief. Ontvangst-, onboarding- of dossierkosten komen er nooit bij.',
      },
      {
        q: 'Is er een minimumaantal bestellingen om met Yaslan te werken?',
        a: 'Nee, geen contractueel minimum. De calculator start bij 100 bestellingen/maand omdat onze degressieve tabel daar het interessantst wordt. Zit je eronder? Contacteer ons: we antwoorden eerlijk, ook als de beste oplossing niet bij ons ligt.',
      },
      {
        q: 'Hoeveel kost levering aan een afhaalpunt met Yaslan?',
        a: 'Vanaf € 6,25 excl. btw per bestelling, alles inbegrepen (transport + pick & pack). Het tarief varieert volgens het gewicht — 0 tot 20 kg — en daalt naarmate je maandvolume stijgt. Boven 25 referenties in je catalogus geldt een aanpassing van € 0,05 tot € 0,50 per bestelling.',
      },
      {
        q: 'Rekenen jullie ontvangst- of inslagkosten aan?',
        a: 'Nee. De ontvangst van je goederen, de kwaliteitscontrole en de inslag zijn inbegrepen, zonder kosten. Je betaalt enkel het transport en de pick & pack van je verzonden bestellingen.',
      },
      {
        q: 'Rekenen jullie kosten aan voor de ontvangst van pallets? (veel logistieke dienstverleners factureren € 5–10 per pallet)',
        a: 'Nee: de ontvangst van je pallets — lossen, kwaliteitscontrole en inslag — is inbegrepen, aan € 0. Waar veel dienstverleners € 5 tot € 10 per ontvangen pallet aanrekenen, factureert Yaslan de ontvangst nooit. Je betaalt enkel het transport en de pick & pack van je verzonden bestellingen.',
      },
      {
        q: 'En de opslag, hoe wordt die gefactureerd?',
        a: 'Die wordt niet apart gefactureerd. Ontvangst, inslag en inventaris zijn inbegrepen: je betaalt enkel het transport en de pick & pack van elke verzonden bestelling. De verschillende opslagmethodes vind je op onze Opslag-pagina.',
      },
      {
        q: 'Verandert de prijs naargelang gewicht of afmeting?',
        a: 'Ja, volgens gewicht: de tabel dekt acht klassen, van 0–1 kg tot 15–20 kg. Boven 20 kg, of voor erg volumineuze pakketten, maken we een offerte met de meest geschikte vervoerder.',
      },
      {
        q: 'Kan ik na de calculator een offerte op maat aanvragen?',
        a: 'Ja. De calculator geeft een betrouwbare grootteorde; de exacte offerte houdt rekening met je bestemmingen, leveropties (thuis, express, internationaal) en gepersonaliseerde verpakking. Antwoord binnen 4 werkuren.',
      },
    ],

    linksTitle: 'Wat deze tarieven dekken',
    linksSubtitle:
      'Elke bestelling passeert langs onze vier diensten — allemaal volgens dezelfde logica: transparant en degressief.',
    links: [
      { key: 'service.storage', label: 'Opslag', desc: 'Ontvangst, inslag, inventaris — inbegrepen.' },
      { key: 'service.pickpack', label: 'Pick & Pack', desc: 'Klaargemaakt in < 4 u, in jouw merkstijl.' },
      { key: 'service.shipping', label: 'Levering volgende dag', desc: 'Cut-off 15u, 30+ vervoerders vergeleken.' },
      { key: 'service.returns', label: 'Retours', desc: 'White-label portaal, snelle herbevoorrading.' },
      { key: 'fulfillment', label: 'DNVB & D2C fulfilment', desc: 'Logistiek gebouwd voor jouw merk.' },
    ],
    linksCta: 'Bekijk de dienst',

    ctaBadge: 'Gratis offerte · Antwoord binnen 4 werkuren',
    ctaTitleLead: 'Een duidelijke prijs zegt meer dan ',
    ctaTitleHighlight: 'duizend beloftes',
    ctaCopy:
      'Stuur ons je volumes en gemiddelde gewichten: je ontvangt een gepersonaliseerde, volledige tarieflijst zonder sterretjes. Vergelijk ze lijn per lijn met je huidige 3PL.',
    ctaPrimary: 'Vraag mijn offerte',
    ctaSecondary: 'Boek een gesprek van 30 min',
    trust1: 'Antwoord binnen 4 werkuren',
    trust2: 'Geen abonnement, geen verbintenis',
    trust3: 'Volledige tarieflijst, nul verborgen kosten',
  },
}
